import { camelize, snakeize } from 'casing'
import { get } from 'lodash'
import {
  errorLogger,
  LoadingTypes,
  startLoading,
  stopLoading,
} from '@labrav/common'

export {
  getFlyOutOnboardingData,
  getFlyoutMicrosoftOnboardingFormData,
  sendFlyOutOnboardingData,
  sendMarketplaceOnboardingData,
} from '@labrav/common'

import {
  fetchSellerData,
  patchCheckMicrosoftConnection,
} from '../../api/marketplace/index'
import type { AppDispatch, RootState } from '../../store/types'
import {
  FlyoutOnboardingForm,
  type CheckMicrosoftConnectionPayload,
  type FlyOutOnboardingChangeType,
  type MicrosoftOnboarding,
  type TestConnectionStatusProps,
} from './types'

export { FlyoutOnboardingForm }
export type { FlyOutOnboardingChangeType }
export { checkAllCompleted } from './utils'
export { processPartnerDetailsData } from './utils'

export const setOnboardingFormData = (
  onboardingFormData: Record<string, unknown>
) => ({
  type: FlyoutOnboardingForm.SET_FLYOUT_ONBOARDING_FORM,
  payload: onboardingFormData,
})

export const updateFlyOutOnboardingData = (
  data: FlyOutOnboardingChangeType
) => ({
  type: FlyoutOnboardingForm.UPDATE_FLYOUT_ONBOARDING_FORM,
  payload: data,
})

export const setFlyOutTestConnectionStatus = (
  status: TestConnectionStatusProps
) => ({
  type: FlyoutOnboardingForm.UPDATE_FLYOUT_TEST_CONNECTION_STATUS,
  payload: status,
})

export const updateMicrosoftOnboardingState = (
  microsoftOnboardingForm: MicrosoftOnboarding
) => ({
  type: FlyoutOnboardingForm.UPDATE_MICROSOFT_ONBOARDING_FORM,
  payload: microsoftOnboardingForm,
})

export const flyOutTestConnection =
  (
    partnerId: string,
    cloudRoleArn: string,
    isMarketplaceCloudSettingsRevampEnabled: boolean,
    updateFlyoutTestConnection: (status: TestConnectionStatusProps) => void
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await updateFlyoutTestConnection({ status: 'loading' })
    const externalId = partnerId
    try {
      const metadataPayload = isMarketplaceCloudSettingsRevampEnabled
        ? { operations: 'test_connection', cloudRoleArn, externalId }
        : { operations: 'test_connection', cloudRoleArn }
      const { data } = await fetchSellerData({
        partnerId,
        metadata: {
          ...snakeize({
            operations: 'test_connection',
            cloudRoleArn,
            externalId,
          }),
          ...snakeize(metadataPayload),
        } as Record<string, unknown>,
      })

      const camelizedData = camelize(data)
      const tested = get(
        camelizedData,
        'operationsResult[0].testConnection.connected',
        false
      )

      const statusValue = tested ? 'success' : 'failure'
      await updateFlyoutTestConnection({ status: statusValue })
    } catch (error) {
      await updateFlyoutTestConnection({ status: 'failure' })
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    }
  }

export const flyOutTestConnectionRoleAndDatafeeds =
  (
    partnerId: string,
    cloudRoleArn: string,
    datafeedsS3BucketName: string,
    updateStatus: (status: TestConnectionStatusProps) => void
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await updateStatus({ status: 'loading' })
    const externalId = partnerId
    try {
      const { data } = await fetchSellerData({
        partnerId,
        metadata: {
          ...snakeize({
            operations: 'test_connection',
            cloudRoleArn,
            externalId,
            datafeedsS3BucketName,
          }),
        } as Record<string, unknown>,
      })
      const camelizedData = camelize(data)
      const tested = get(
        camelizedData,
        'operationsResult[0].testConnection.connected',
        false
      )

      const statusValue = tested ? 'success' : 'failure'
      await updateStatus({ status: statusValue })
    } catch (error) {
      await updateStatus({ status: 'failure' })
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    }
  }

export const checkMicrosoftConnection =
  (
    partnerId: string,
    testConnection: boolean,
    payload: CheckMicrosoftConnectionPayload
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.CHECK_MICROSOFT_CONNECTION))
    try {
      const response = await patchCheckMicrosoftConnection(
        partnerId,
        testConnection,
        payload
      )
      const statusCode = response.status
      if (statusCode === 200) {
        return {
          status: 'success',
        }
      } else {
        return {
          status: 'failure',
        }
      }
    } catch (error) {
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
      return {
        status: 'failure',
      }
    } finally {
      await dispatch(stopLoading(LoadingTypes.CHECK_MICROSOFT_CONNECTION))
    }
  }
